import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  DatePicker,
  Select,
  Space,
  message,
  Divider,
  InputNumber,
  Tag,
  Input,
  Drawer,
  Descriptions,
  Typography,
  Alert,
  Radio,
  Input as AntInput,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  rentalReturns,
  rentals as rentApi,
  customers as custApi,
} from "../api";
import PageHeader from "../components/common/PageHeader";
import StatusBadge from "../components/common/StatusBadge";
import { fmtCurrency, fmtDate } from "../utils/format";
import dayjs from "dayjs";

const { Text } = Typography;

// ── Secret key to unlock Edit / Delete ──────────────────────────
const SECRET_KEY = "admin123";

const CONDITION_OPTIONS = [
  { value: "GOOD", label: "✅ Good", color: "green" },
  { value: "PARTIAL_DAMAGE", label: "⚠️ Partial Damage", color: "orange" },
  { value: "FULL_DAMAGE", label: "❌ Full Damage", color: "red" },
];

const conditionTag = (c) => {
  const o = CONDITION_OPTIONS.find((x) => x.value === c);
  return o ? <Tag color={o.color}>{o.label}</Tag> : <Tag>{c}</Tag>;
};

const destTag = (d) => {
  if (d === "WAREHOUSE") return <Tag color="green">Warehouse</Tag>;
  if (d === "SERVICE_CENTER") return <Tag color="orange">Service Center</Tag>;
  if (d === "SCRAP") return <Tag color="red">Scrap</Tag>;
  return <Tag>{d}</Tag>;
};

function fmtCur(v) {
  return (
    "₹" + Number(v ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })
  );
}

/** Full-day ceiling billing — matches backend RentCalculator exactly. */
function calcDays(fromDt, toDt) {
  if (!fromDt || !toDt) return 0;
  const minutesHeld = toDt.diff(fromDt, "minute");
  if (minutesHeld <= 0) return 0;
  return Math.ceil(minutesHeld / (24 * 60));
}

export default function RentalReturns() {
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [viewRec, setViewRec] = useState(null);
  const [allCusts, setAllCusts] = useState([]);
  const [custRentals, setCustRentals] = useState([]);
  const [rentalDetail, setRentalDetail] = useState(null);
  const [lines, setLines] = useState([]);
  const [page, setPage] = useState(0);
  const [returnDateVal, setReturnDateVal] = useState(dayjs());
  const [form] = Form.useForm();

  // ── Filter state ───────────────────────────────────────────────
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState([]);

  // ── Edit modal state ──────────────────────────────────────────
  const [editRec, setEditRec] = useState(null); // record being edited
  const [editForm] = Form.useForm();

  // ── Secret key modal state ────────────────────────────────────
  const [secretModal, setSecretModal] = useState(false);
  const [secretInput, setSecretInput] = useState("");
  const [secretError, setSecretError] = useState("");
  const [secretAction, setSecretAction] = useState(null); // { type: 'edit'|'delete', rec }
  const [unlocked, setUnlocked] = useState(false); // stays true for the session
  const [deleteConfirmRec, setDeleteConfirmRec] = useState(null); // record pending delete confirm

  // ── Load ──────────────────────────────────────────────────────
  const load = useCallback(async (pg = page, search = q, status = filterStatus, range = filterDateRange) => {
    setLoading(true);
    try {
      const r = await rentalReturns.list({
        page: pg, size: 20,
        q: search || undefined,
        status: status || undefined,
        fromDate: range?.length === 2 ? range[0].format("YYYY-MM-DD") : undefined,
        toDate:   range?.length === 2 ? range[1].format("YYYY-MM-DD") : undefined,
      });
      setData(r.data.data);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { custApi.all().then((r) => setAllCusts(r.data.data)); }, []);

  const handleSearchQ = (val) => { setQ(val); setPage(0); load(0, val, filterStatus, filterDateRange); };
  const handleFilterStatus = (val) => { setFilterStatus(val); setPage(0); load(0, q, val, filterDateRange); };
  const handleFilterDateRange = (range) => { setFilterDateRange(range || []); setPage(0); load(0, q, filterStatus, range || []); };;

  // ── Secret key gate ───────────────────────────────────────────
  /**
   * Call this before any destructive action.
   * If already unlocked for this session, runs the action immediately.
   * Otherwise shows the secret key modal; on success runs the action.
   */
  const requireSecret = (action) => {
    if (unlocked) {
      action();
      return;
    }
    setSecretInput("");
    setSecretError("");
    setSecretAction(() => action);
    setSecretModal(true);
  };

  const onSecretConfirm = () => {
    if (secretInput === SECRET_KEY) {
      setUnlocked(true);
      setSecretModal(false);
      secretAction && secretAction();
    } else {
      setSecretError("Incorrect key. Try again.");
    }
  };

  // ── Create flow ───────────────────────────────────────────────
  const onCustomerSelect = async (cid) => {
    form.setFieldValue("rentalId", null);
    setCustRentals([]);
    setLines([]);
    setRentalDetail(null);
    const r = await rentApi.list({ customerId: cid, size: 100 });
    const all = r.data.data.content || [];
    const outstanding = all.filter((rental) =>
      (rental.items || []).some(
        (i) => Number(i.qtyRented) - Number(i.qtyReturned) > 0,
      ),
    );
    setCustRentals(outstanding);
  };

  const onRentalSelect = async (rid) => {
    const r = await rentApi.get(rid);
    const rental = r.data.data;
    setRentalDetail(rental);

    const issueDateTime = rental.rentalDateTime
      ? dayjs(rental.rentalDateTime)
      : dayjs(rental.rentalDate).startOf("day");

    const outstandingItems = (rental.items || []).filter(
      (i) => Number(i.qtyRented) - Number(i.qtyReturned) > 0,
    );

    const retDt = form.getFieldValue("returnDate") || dayjs();

    setLines(
      outstandingItems.map((i) => {
        const autodays = calcDays(issueDateTime, retDt);
        return {
          rentalItemId: i.id,
          productName: i.product?.name,
          productUnit: i.product?.unit,
          qtyRented: Number(i.qtyRented),
          qtyAlreadyReturned: Number(i.qtyReturned),
          maxQty: Number(i.qtyRented) - Number(i.qtyReturned),
          qtyReturning: 0,
          fromDateTime: issueDateTime,
          daysOverride: autodays,
          condition: "GOOD",
          damageAmount: 0,
          damageDescription: "",
          destination: "WAREHOUSE",
        };
      }),
    );
  };

  const onReturnDateChange = (dt) => {
    setReturnDateVal(dt);
    setLines((prev) =>
      prev.map((l) => ({
        ...l,
        daysOverride: calcDays(l.fromDateTime, dt),
      })),
    );
  };

  const updateLine = (i, field, val) => {
    setLines((prev) => {
      const l = [...prev];
      l[i] = { ...l[i], [field]: val };
      if (field === "condition") {
        if (val === "GOOD") {
          l[i].destination = "WAREHOUSE";
          l[i].damageAmount = 0;
        }
        if (val === "PARTIAL_DAMAGE") l[i].destination = "SERVICE_CENTER";
        if (val === "FULL_DAMAGE") l[i].destination = "SCRAP";
      }
      // Enforce shared pool for split sub-lines
      if (field === "qtyReturning" && l[i]._splitId !== undefined) {
        const pool = l[i]._splitPool ?? l[i].maxQty;
        const usedByOthers = l
          .filter((r, ri) => ri !== i && r._splitId === l[i]._splitId)
          .reduce((s, r) => s + Number(r.qtyReturning || 0), 0);
        const remaining = Math.max(0, pool - usedByOthers);
        l[i].qtyReturning = Math.min(Number(val || 0), remaining);
      }
      return l;
    });
  };

  // ── Split helpers ─────────────────────────────────────────────
  // Each product line can optionally be split into multiple condition sub-boxes.
  // A stable _splitId string (not index) ties sub-lines together.
  // The shared pool = maxQty; each box shows max = pool - sum(other boxes).
  // User can add a box (up to 3 total), remove any individual box,
  // or collapse all boxes back to a single line.

  const splitLine = (i) => {
    setLines((prev) => {
      const l = [...prev];
      const original = l[i];
      const splitId = `split-${Date.now()}-${i}`;
      // Start with 2 boxes — user can add a 3rd anytime
      const subLines = [
        {
          ...original,
          qtyReturning: 0,
          condition: "GOOD",
          destination: "WAREHOUSE",
          damageAmount: 0,
          damageDescription: "",
          _splitId: splitId,
          _splitPool: original.maxQty,
        },
        {
          ...original,
          qtyReturning: 0,
          condition: "PARTIAL_DAMAGE",
          destination: "SERVICE_CENTER",
          damageAmount: 0,
          damageDescription: "",
          _splitId: splitId,
          _splitPool: original.maxQty,
        },
      ];
      l.splice(i, 1, ...subLines);
      return l;
    });
  };

  // Add a 3rd box to a split group (only possible when group has < 3 boxes)
  const addSplitBox = (splitId) => {
    setLines((prev) => {
      const l = [...prev];
      const template = l.find((r) => r._splitId === splitId);
      if (!template) return l;
      let lastIdx = -1;
      l.forEach((r, idx) => { if (r._splitId === splitId) lastIdx = idx; });
      const newLine = {
        ...template,
        qtyReturning: 0,
        condition: "GOOD",
        destination: "WAREHOUSE",
        damageAmount: 0,
        damageDescription: "",
        _splitId: splitId,
      };
      l.splice(lastIdx + 1, 0, newLine);
      return l;
    });
  };

  // Remove one individual box from a split group.
  // If only 1 box would remain, collapse it back to a normal single line.
  const removeSplitBox = (i) => {
    setLines((prev) => {
      const l = [...prev];
      const splitId = l[i]._splitId;
      const siblings = l.filter((r) => r._splitId === splitId);
      if (siblings.length <= 2) {
        // Collapse: remove all sub-lines, restore as single unsplit line
        const firstSib = siblings[0];
        const restored = { ...firstSib };
        delete restored._splitId;
        delete restored._splitPool;
        restored.qtyReturning = 0;
        restored.condition = "GOOD";
        restored.destination = "WAREHOUSE";
        restored.damageAmount = 0;
        restored.damageDescription = "";
        // Find first index of this split group and replace all
        const firstIdx = l.findIndex((r) => r._splitId === splitId);
        const filtered = l.filter((r) => r._splitId !== splitId);
        filtered.splice(firstIdx, 0, restored);
        return filtered;
      }
      // Just remove this one box
      l.splice(i, 1);
      return l;
    });
  };

  // Collapse all boxes back to a single line
  const unsplitLine = (splitId) => {
    setLines((prev) => {
      const l = [...prev];
      const firstIdx = l.findIndex((r) => r._splitId === splitId);
      const template = l.find((r) => r._splitId === splitId);
      if (!template) return l;
      const restored = { ...template };
      delete restored._splitId;
      delete restored._splitPool;
      restored.qtyReturning = 0;
      restored.condition = "GOOD";
      restored.destination = "WAREHOUSE";
      restored.damageAmount = 0;
      restored.damageDescription = "";
      const filtered = l.filter((r) => r._splitId !== splitId);
      filtered.splice(firstIdx, 0, restored);
      return filtered;
    });
  };

  const onSave = async () => {
    try {
      const vals = await form.validateFields();
      const returningLines = lines.filter((l) => l.qtyReturning > 0);
      if (returningLines.length === 0) {
        message.error("Enter return quantity for at least one item");
        return;
      }
      const returnDt = vals.returnDate;
      const payload = {
        customerId: vals.customerId,
        returnDate: returnDt.format("YYYY-MM-DDTHH:mm:ss"),
        status: "PENDING",
        offer: vals.offer || 0,
        notes: vals.notes,
        items: returningLines.map((l) => {
          const effectiveFrom = returnDt.subtract(l.daysOverride, "day");
          return {
            rentalItemId: l.rentalItemId,
            qtyReturned: l.qtyReturning,
            fromDate: effectiveFrom.format("YYYY-MM-DDTHH:mm:ss"),
            condition: l.condition,
            damageAmount: l.damageAmount || 0,
            damageDescription: l.damageDescription || "",
            destination: l.destination,
          };
        }),
      };
      await rentalReturns.create(payload);
      message.success("Return processed successfully");
      setModal(false);
      load();
    } catch (e) {
      console.error(e);
      message.error("Error processing return");
    }
  };

  const openModal = () => {
    form.resetFields();
    const now = dayjs();
    form.setFieldsValue({ returnDate: now });
    setReturnDateVal(now);
    setCustRentals([]);
    setLines([]);
    setRentalDetail(null);
    setModal(true);
  };

  // ── Edit flow ─────────────────────────────────────────────────
  // A return is only "locked" if real cash has been recorded against it via the
  // Payments page (paidAmount > 0). Status alone is NOT a safe check here —
  // status can be CLOSED purely because the rental's advance share covers the
  // bill, with zero actual payment rows to delete. Using status would make the
  // record permanently undeletable/uneditable with no way to "unlock" it.
  const isLocked = (rec) => Number(rec.paidAmount || 0) > 0;

  const openEdit = (rec) => {
    if (isLocked(rec)) {
      message.warning(
        "This return has a payment recorded and cannot be edited. Delete the payment first to unlock."
      );
      return;
    }
    requireSecret(() => {
      setEditRec(rec);
      editForm.setFieldsValue({
        offer: Number(rec.offer || 0),
        notes: rec.notes || "",
      });
    });
  };

  const onEditSave = async () => {
    try {
      const vals = await editForm.validateFields();
      await rentalReturns.update(editRec.id, {
        offer: vals.offer || 0,
        notes: vals.notes || "",
      });
      message.success("Return updated");
      setEditRec(null);
      load();
    } catch (e) {
      console.error(e);
      message.error("Update failed");
    }
  };

  // ── Delete flow ───────────────────────────────────────────────
  // Step 1: secret key gate → Step 2: confirm modal → Step 3: actual delete
  const openDelete = (rec) => {
    if (isLocked(rec)) {
      message.warning(
        "This return has a payment recorded and cannot be deleted. Delete the payment first to unlock."
      );
      return;
    }
    requireSecret(() => setDeleteConfirmRec(rec));
  };

  const confirmDelete = async () => {
    const rec = deleteConfirmRec;
    setDeleteConfirmRec(null);
    try {
      await rentalReturns.delete(rec.id);
      message.success(`Return ${rec.returnNo} deleted and reversed`);
      load();
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.message || "Delete failed");
    }
  };

  // ── Table columns ─────────────────────────────────────────────
  const cols = [
    {
      title: "Return No",
      dataIndex: "returnNo",
      render: (v) => <span className="mono text-info">{v}</span>,
    },
    {
      title: "Customer",
      dataIndex: ["customer", "name"],
      render: (v) => <strong>{v}</strong>,
    },
    { title: "Return Date", dataIndex: "returnDate", render: fmtDate },
    {
      title: "Total Bill",
      dataIndex: "totalBill",
      render: (v) => <span className="mono">{fmtCurrency(v)}</span>,
    },
    {
      title: "Offer",
      dataIndex: "offer",
      render: (v) =>
        v > 0 ? (
          <span className="mono" style={{ color: "var(--warning)" }}>
            -{fmtCurrency(v)}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "Net Bill",
      dataIndex: "netBill",
      render: (v) => (
        <span className="mono" style={{ fontWeight: 600 }}>
          {fmtCurrency(v)}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "status",
      render: (v) => <StatusBadge status={v} />,
    },
    {
      title: "Actions",
      key: "a",
      render: (_, r) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setViewRec(r)}
          >
            View
          </Button>

          <Button
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => printBill(r)}
          >
            Print
          </Button>

          <Button
            size="small"
            icon={isLocked(r) ? <LockOutlined /> : <EditOutlined />}
            onClick={() => openEdit(r)}
            disabled={false}
          >
            Edit
          </Button>

          <Button
            size="small"
            danger
            icon={isLocked(r) ? <LockOutlined /> : <DeleteOutlined />}
            onClick={() => openDelete(r)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // ── Print Bill ────────────────────────────────────────────────
  const printBill = (rec) => {
    if (!rec) return;
    const rentTotal = (rec.items || []).reduce(
      (s, r) => s + Number(r.cost || 0),
      0,
    );
    const damageTotal = (rec.items || []).reduce(
      (s, r) => s + Number(r.damageAmount || 0),
      0,
    );
    const fmt = (v) =>
      "₹" +
      Number(v ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
    const fmtD = (v) =>
      v
        ? new Date(v).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "—";
    const condLabel = {
      GOOD: "Good",
      PARTIAL_DAMAGE: "Partial Damage",
      FULL_DAMAGE: "Full Damage",
    };
    const destLabel = {
      WAREHOUSE: "Warehouse",
      SERVICE_CENTER: "Service Center",
      SCRAP: "Scrap",
    };

    const itemRows = (rec.items || [])
      .map(
        (item) => `
      <tr>
        <td>${item.rentalItem?.product?.name || "—"}</td>
        <td style="text-align:center">${item.qtyReturned}</td>
        <td>${fmtD(item.fromDate)} → ${fmtD(item.toDate)}</td>
        <td style="text-align:right">${fmt(item.cost)}</td>
        <td style="text-align:right;color:${Number(item.damageAmount) > 0 ? "#c0392b" : "#888"}">${Number(item.damageAmount) > 0 ? fmt(item.damageAmount) : "—"}</td>
        <td>${condLabel[item.condition] || item.condition}</td>
        <td>${destLabel[item.destination] || item.destination}</td>
      </tr>`,
      )
      .join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Return Bill — ${rec.returnNo}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 32px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
    .brand { font-size: 28px; font-weight: 800; color: #1a56db; letter-spacing: -0.5px; }
    .doc-title { font-size: 13px; color: #666; margin-top: 4px; }
    .bill-meta { text-align: right; font-size: 12px; color: #555; line-height: 1.8; }
    .bill-meta strong { font-size: 18px; color: #222; display: block; }
    hr { border: none; border-top: 2px solid #1a56db; margin-bottom: 20px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
    .info-box { background: #f8faff; border: 1px solid #dde6ff; border-radius: 6px; padding: 12px 16px; }
    .info-box label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px; }
    .info-box span { font-size: 14px; font-weight: 600; color: #111; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
    thead tr { background: #1a56db; color: #fff; }
    thead th { padding: 8px 10px; text-align: left; font-weight: 600; }
    tbody tr:nth-child(even) { background: #f4f7ff; }
    tbody td { padding: 7px 10px; border-bottom: 1px solid #e8edf5; vertical-align: middle; }
    .totals { margin-left: auto; width: 290px; }
    .totals table { margin-bottom: 0; }
    .totals td { padding: 5px 10px; border: none; }
    .totals td:last-child { text-align: right; font-weight: 600; }
    .totals .net-row td { font-size: 15px; color: #1a56db; font-weight: 800; border-top: 2px solid #1a56db; padding-top: 8px; }
    .status-badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    .PENDING { background: #fff3e0; color: #e65100; }
    .PARTIAL { background: #e3f2fd; color: #1565c0; }
    .CLOSED  { background: #e8f5e9; color: #2e7d32; }
    .footer { margin-top: 32px; font-size: 11px; color: #aaa; text-align: center; border-top: 1px solid #eee; padding-top: 12px; }
    @media print {
      body { padding: 16px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">RentOps</div>
      <div class="doc-title">Rental Return Bill</div>
    </div>
    <div class="bill-meta">
      <strong>${rec.returnNo}</strong>
      Return Date: ${fmtD(rec.returnDate)}<br/>
      Status: <span class="status-badge ${rec.status}">${rec.status}</span>
    </div>
  </div>
  <hr/>
  <div class="info-grid">
    <div class="info-box"><label>Customer</label><span>${rec.customer?.name || "—"}</span></div>
    <div class="info-box"><label>Phone</label><span>${rec.customer?.phone || "—"}</span></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Product</th>
        <th style="text-align:center">Qty</th>
        <th>Period</th>
        <th style="text-align:right">Rent Cost</th>
        <th style="text-align:right">Damage</th>
        <th>Condition</th>
        <th>Destination</th>
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>
  <div class="totals">
    <table>
      <tr><td>Rent Total</td><td>${fmt(rentTotal)}</td></tr>
      ${damageTotal > 0 ? `<tr><td style="color:#c0392b">Damage / Repair</td><td style="color:#c0392b">${fmt(damageTotal)}</td></tr>` : ""}
      <tr><td>Gross Total</td><td>${fmt(rec.totalBill)}</td></tr>
      ${Number(rec.offer) > 0 ? `<tr><td style="color:#d97706">Offer / Discount</td><td style="color:#d97706">- ${fmt(rec.offer)}</td></tr>` : ""}
      <tr class="net-row"><td>Net Payable</td><td>${fmt(rec.netBill)}</td></tr>
      <tr><td style="color:#888">Paid</td><td style="color:#888">${fmt(rec.paidAmount)}</td></tr>
      <tr><td style="color:#c0392b;font-weight:700">Balance Due</td>
          <td style="color:#c0392b;font-weight:700">${fmt(Math.max(0, Number(rec.netBill) - Number(rec.paidAmount)))}</td></tr>
    </table>
  </div>
  ${rec.notes ? `<div style="margin-top:16px;font-size:12px;color:#555"><strong>Notes:</strong> ${rec.notes}</div>` : ""}
  <div class="footer">Generated by RentOps &nbsp;·&nbsp; ${new Date().toLocaleString("en-IN")}</div>
  <div class="no-print" style="margin-top:24px;text-align:center">
    <button onclick="window.print()" style="padding:10px 32px;background:#1a56db;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer">
      🖨 Print / Save as PDF
    </button>
  </div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=900,height=700");
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => {
        win.focus();
        win.print();
      }, 400);
    }
  };

  return (
    <div>
      <PageHeader
        title="Rental Returns"
        subtitle="Process partial or full customer returns with condition tracking"
        actions={[
          <Button
            key="a"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openModal}
          >
            Process Return
          </Button>,
        ]}
      />

      {/* ── Filters ────────────────────────────── */}
      <Space wrap style={{ marginBottom: 16 }}>
        <AntInput
          prefix={<SearchOutlined />}
          placeholder="Search return no, customer..."
          value={q}
          onChange={(e) => handleSearchQ(e.target.value)}
          allowClear
          style={{ width: 260 }}
        />
        <Select
          placeholder="Payment Status"
          allowClear
          value={filterStatus}
          onChange={handleFilterStatus}
          style={{ width: 150 }}
          options={["PENDING","PARTIAL","CLOSED"].map(s => ({ value: s, label: s }))}
        />
        <DatePicker.RangePicker
          value={filterDateRange}
          onChange={handleFilterDateRange}
          style={{ width: 260 }}
        />
      </Space>

      <Table
        scroll={{ x: "max-content" }}
        columns={cols}
        dataSource={data.content}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          total: data.totalElements,
          pageSize: 20,
          current: page + 1,
          onChange: (p) => setPage(p - 1),
        }}
      />

      {/* ── Secret Key Modal ─────────────────────────────────── */}
      <Modal
        open={secretModal}
        title={
          <span>
            <LockOutlined style={{ marginRight: 8 }} />
            Enter Secret Key
          </span>
        }
        onOk={onSecretConfirm}
        onCancel={() => setSecretModal(false)}
        okText="Unlock"
        width={360}
      >
        <p style={{ color: "var(--text-muted)", marginBottom: 12 }}>
          This action is protected. Enter the admin secret key to proceed.
        </p>
        <AntInput.Password
          placeholder="Secret key"
          value={secretInput}
          onChange={(e) => {
            setSecretInput(e.target.value);
            setSecretError("");
          }}
          onPressEnter={onSecretConfirm}
          autoFocus
          status={secretError ? "error" : ""}
        />
        {secretError && (
          <div style={{ color: "#f5222d", fontSize: 12, marginTop: 6 }}>
            {secretError}
          </div>
        )}
      </Modal>

      {/* ── Edit Modal ───────────────────────────────────────── */}
      <Modal
        open={!!editRec}
        title={`Edit Return — ${editRec?.returnNo}`}
        onOk={onEditSave}
        onCancel={() => setEditRec(null)}
        okText="Save Changes"
        width={420}
      >
        <Alert
          type="warning"
          style={{ marginBottom: 14 }}
          message="Items cannot be changed after creation. You can only update offer and notes. Payment status is auto-calculated from actual payments."
        />
        <Form form={editForm} layout="vertical">
          <Form.Item name="offer" label="Offer / Discount (₹)">
            <InputNumber min={0} prefix="₹" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
        {editRec && (
          <div
            style={{
              fontSize: 12,
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
            }}
          >
            Total bill: {fmtCur(editRec.totalBill)} &nbsp;|&nbsp; Net bill after
            offer:{" "}
            {fmtCur(
              Math.max(
                0,
                Number(editRec.totalBill) -
                  Number(editForm.getFieldValue("offer") ?? editRec.offer ?? 0),
              ),
            )}
          </div>
        )}
      </Modal>

      {/* ── Delete Confirm Modal ─────────────────────────────── */}
      <Modal
        open={!!deleteConfirmRec}
        title={
          <span style={{ color: "#f5222d" }}>
            <DeleteOutlined style={{ marginRight: 8 }} />
            Confirm Delete
          </span>
        }
        onOk={confirmDelete}
        onCancel={() => setDeleteConfirmRec(null)}
        okText="Yes, Delete & Reverse"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        width={420}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteConfirmRec?.returnNo}</strong>?
        </p>
        <Alert
          type="error"
          style={{ marginTop: 8 }}
          message="This fully reverses the return — qty_returned is rolled back, service center records and scrap entries are deleted, and customer outstanding is adjusted."
        />
      </Modal>

      {/* ── Create Return Modal ──────────────────────────────── */}
      <Modal
        open={modal}
        title="Process Rental Return"
        onCancel={() => setModal(false)}
        onOk={onSave}
        width={860}
        okText="Save Return"
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical">
          <Space wrap style={{ width: "100%" }} align="start">
            <Form.Item
              name="customerId"
              label="Customer"
              rules={[{ required: true }]}
              style={{ minWidth: 220 }}
            >
              <Select
                showSearch
                placeholder="Select customer"
                style={{ width: 220 }}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                options={allCusts.map((c) => ({
                  value: c.id,
                  label: `${c.name} (${c.phone || ""})`,
                }))}
                onChange={onCustomerSelect}
              />
            </Form.Item>
            <Form.Item name="rentalId" label="Rental (outstanding only)">
              <Select
                placeholder="Select rental"
                style={{ width: 220 }}
                onChange={onRentalSelect}
                options={custRentals.map((r) => ({
                  value: r.id,
                  label: `${r.rentalNo} · ${fmtDate(r.rentalDate)}`,
                }))}
                disabled={custRentals.length === 0}
              />
            </Form.Item>
            <Form.Item
              name="returnDate"
              label="Return Date & Time"
              rules={[{ required: true }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                defaultValue={dayjs()}
                onChange={onReturnDateChange}
              />
            </Form.Item>
          </Space>
          <Space wrap>
            <Form.Item name="offer" label="Offer / Discount (₹)">
              <InputNumber min={0} prefix="₹" style={{ width: 150 }} />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input style={{ width: 220 }} />
            </Form.Item>
          </Space>
        </Form>

        {lines.length > 0 && (
          <>
            <Divider style={{ borderColor: "var(--border)" }}>
              Items Being Returned
              <Text
                type="secondary"
                style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}
              >
                — enter 0 to skip an item (partial return allowed)
              </Text>
            </Divider>

            {lines.map((l, i) => {
              const retDt = returnDateVal || dayjs();
              const autoDays = calcDays(l.fromDateTime, retDt);
              const days = l.daysOverride != null ? l.daysOverride : autoDays;
              const isOverridden = l.daysOverride !== autoDays;

              // Split pool calculations
              const isSplit = l._splitId !== undefined;
              const pool = l._splitPool ?? l.maxQty;
              const siblings = isSplit ? lines.filter((r) => r._splitId === l._splitId) : [];
              const usedByOthers = isSplit
                ? siblings.filter((_, si) => lines.indexOf(siblings[si]) !== i)
                    .reduce((s, r) => s + Number(r.qtyReturning || 0), 0)
                : 0;
              const myMax = isSplit ? Math.max(0, pool - usedByOthers) : l.maxQty;
              const usedTotal = isSplit ? siblings.reduce((s, r) => s + Number(r.qtyReturning || 0), 0) : 0;
              const isFirstInGroup = isSplit && i === lines.findIndex((r) => r._splitId === l._splitId);
              const isLastInGroup = isSplit && i === lines.map((r, ri) => r._splitId === l._splitId ? ri : -1).filter(x => x >= 0).pop();

              return (
                <div
                  key={isSplit ? `${l._splitId}-${i}` : i}
                  style={{
                    marginBottom: isLastInGroup || !isSplit ? 14 : 4,
                    padding: "12px 14px",
                    background: "var(--surface2, #fafafa)",
                    border: "1px solid var(--border, #e8e8e8)",
                    borderRadius: 8,
                    borderLeft: `4px solid ${
                      l.condition === "GOOD"
                        ? "#52c41a"
                        : l.condition === "PARTIAL_DAMAGE"
                          ? "#fa8c16"
                          : "#f5222d"
                    }`,
                  }}
                >
                  {/* Product header — only on first line of group (or unsplit) */}
                  {(!isSplit || isFirstInGroup) && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <Text
                          strong
                          style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}
                        >
                          {l.productName}
                        </Text>
                        <Tag style={{ marginLeft: 8 }}>{l.productUnit}</Tag>
                        <Tag color="blue">Rented: {l.qtyRented}</Tag>
                        <Tag color="green">
                          Returned so far: {l.qtyAlreadyReturned}
                        </Tag>
                        <Tag color="orange">Outstanding: {l.maxQty}</Tag>
                      </div>
                      {l.fromDateTime && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Issued: {l.fromDateTime.format("DD MMM YYYY HH:mm")}
                        </Text>
                      )}
                    </div>
                  )}

                  {/* Split group pool bar — shown on every sub-line */}
                  {isSplit && (
                    <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        Box {siblings.indexOf(l) + 1} of {siblings.length}
                      </span>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: usedTotal === pool ? "#52c41a" : "#fa8c16",
                      }}>
                        Pool: {usedTotal} / {pool} allocated
                        {usedTotal < pool && ` · ${pool - usedTotal} remaining`}
                        {usedTotal === pool && " ✓"}
                      </span>
                      {isFirstInGroup && (
                        <Button
                          size="small"
                          type="link"
                          danger
                          style={{ fontSize: 11, padding: 0 }}
                          onClick={() => unsplitLine(l._splitId)}
                        >
                          Collapse all
                        </Button>
                      )}
                      {/* Add box button — on last box, only if < 3 boxes */}
                      {isLastInGroup && siblings.length < 3 && (
                        <Button
                          size="small"
                          type="dashed"
                          style={{ fontSize: 11, marginLeft: "auto" }}
                          onClick={() => addSplitBox(l._splitId)}
                        >
                          + Add box
                        </Button>
                      )}
                      {/* Remove this box button (always visible on each sub-line) */}
                      <Button
                        size="small"
                        type="link"
                        danger
                        style={{ fontSize: 11, padding: 0, marginLeft: isLastInGroup && siblings.length < 3 ? 0 : "auto" }}
                        onClick={() => removeSplitBox(i)}
                      >
                        ✕ Remove box
                      </Button>
                    </div>
                  )}

                  <Space wrap align="start">
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-muted)",
                          marginBottom: 3,
                        }}
                      >
                        Returning Now
                      </div>
                      <InputNumber
                        min={0}
                        max={myMax}
                        step={0.5}
                        value={l.qtyReturning}
                        onChange={(v) => updateLine(i, "qtyReturning", v ?? 0)}
                        style={{ width: 100 }}
                      />
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--text-muted)",
                          marginTop: 2,
                        }}
                      >
                        {isSplit
                          ? `max ${myMax} (pool: ${pool})`
                          : `max ${l.maxQty}`}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-muted)",
                          marginBottom: 3,
                        }}
                      >
                        Days Charged
                        {isOverridden && (
                          <span style={{ color: "#fa8c16", marginLeft: 4 }}>
                            (overridden)
                          </span>
                        )}
                      </div>
                      <InputNumber
                        min={0}
                        step={1}
                        precision={0}
                        value={days}
                        onChange={(v) => updateLine(i, "daysOverride", v ?? 0)}
                        style={{ width: 90 }}
                      />
                      {isOverridden ? (
                        <div
                          style={{
                            fontSize: 10,
                            color: "#1677ff",
                            marginTop: 2,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            updateLine(i, "daysOverride", autoDays)
                          }
                        >
                          ↺ reset to {autoDays}
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: 10,
                            color: "var(--text-muted)",
                            marginTop: 2,
                          }}
                        >
                          auto-calculated
                        </div>
                      )}
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-muted)",
                          marginBottom: 3,
                        }}
                      >
                        Condition
                      </div>
                      {/* Split button — only show on unsplit lines */}
                      {!isSplit && (
                        <div style={{ marginBottom: 8 }}>
                          <Button
                            size="small"
                            type="dashed"
                            onClick={() => splitLine(i)}
                            style={{ fontSize: 11 }}
                          >
                            ✂️ Split by condition
                          </Button>
                        </div>
                      )}
                      <Radio.Group
                        value={l.condition}
                        onChange={(e) =>
                          updateLine(i, "condition", e.target.value)
                        }
                        buttonStyle="solid"
                        size="small"
                      >
                        <Radio.Button value="GOOD">
                          <CheckCircleOutlined /> Good
                        </Radio.Button>
                        <Radio.Button value="PARTIAL_DAMAGE">
                          <WarningOutlined /> Partial
                        </Radio.Button>
                        <Radio.Button value="FULL_DAMAGE">
                          <CloseCircleOutlined /> Full Damage
                        </Radio.Button>
                      </Radio.Group>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-muted)",
                          marginBottom: 3,
                        }}
                      >
                        Destination
                      </div>
                      <div style={{ paddingTop: 5 }}>
                        {destTag(l.destination)}
                      </div>
                    </div>
                  </Space>

                  {(l.condition === "PARTIAL_DAMAGE" ||
                    l.condition === "FULL_DAMAGE") && (
                    <Space wrap style={{ marginTop: 10 }}>
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-muted)",
                            marginBottom: 3,
                          }}
                        >
                          {l.condition === "FULL_DAMAGE"
                            ? "Damage Charge (₹)"
                            : "Repair Charge (₹)"}
                        </div>
                        <InputNumber
                          min={0}
                          prefix="₹"
                          value={l.damageAmount}
                          onChange={(v) =>
                            updateLine(i, "damageAmount", v ?? 0)
                          }
                          style={{ width: 160 }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-muted)",
                            marginBottom: 3,
                          }}
                        >
                          Damage Description
                        </div>
                        <Input
                          value={l.damageDescription}
                          onChange={(e) =>
                            updateLine(i, "damageDescription", e.target.value)
                          }
                          placeholder="Describe the damage..."
                          style={{ width: 260 }}
                        />
                      </div>
                    </Space>
                  )}

                  {l.qtyReturning > 0 && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "8px 10px",
                        background: "var(--surface3, #f0f5ff)",
                        borderRadius: 6,
                        fontSize: 12,
                        display: "flex",
                        gap: 20,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <span>
                        📦{" "}
                        <strong>
                          {days} day{days !== 1 ? "s" : ""}
                        </strong>{" "}
                        × qty {l.qtyReturning}
                        <span
                          style={{ color: "var(--text-muted)", marginLeft: 4 }}
                        >
                          (rent cost calculated on save)
                        </span>
                      </span>
                      {l.damageAmount > 0 && (
                        <span style={{ color: "#f5222d" }}>
                          + 🔧 Damage: {fmtCur(l.damageAmount)}
                        </span>
                      )}
                      <span
                        style={{
                          marginLeft: "auto",
                          fontSize: 11,
                          color: "var(--text-muted)",
                        }}
                      >
                        {l.condition === "GOOD" && "✅ → Warehouse"}
                        {l.condition === "PARTIAL_DAMAGE" &&
                          `🔧 → Service Center · customer pays ${fmtCur(l.damageAmount || 0)} repair`}
                        {l.condition === "FULL_DAMAGE" &&
                          `🗑 → Scrap · customer pays ${fmtCur(l.damageAmount || 0)} damage`}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}

            {lines.some((l) => l.qtyReturning > 0) &&
              (() => {
                const totalDamage = lines.reduce(
                  (s, l) =>
                    s + (l.qtyReturning > 0 ? Number(l.damageAmount || 0) : 0),
                  0,
                );
                return (
                  <Alert
                    type="info"
                    style={{ marginTop: 8 }}
                    message={
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 12,
                          display: "flex",
                          gap: 16,
                          flexWrap: "wrap",
                        }}
                      >
                        <span>
                          Lines:{" "}
                          <strong>
                            {lines.filter((l) => l.qtyReturning > 0).length}
                          </strong>
                        </span>
                        <span style={{ color: "#52c41a" }}>
                          Warehouse:{" "}
                          {
                            lines.filter(
                              (l) =>
                                l.qtyReturning > 0 &&
                                l.destination === "WAREHOUSE",
                            ).length
                          }
                        </span>
                        <span style={{ color: "#fa8c16" }}>
                          Service:{" "}
                          {
                            lines.filter(
                              (l) =>
                                l.qtyReturning > 0 &&
                                l.destination === "SERVICE_CENTER",
                            ).length
                          }
                        </span>
                        <span style={{ color: "#f5222d" }}>
                          Scrap:{" "}
                          {
                            lines.filter(
                              (l) =>
                                l.qtyReturning > 0 && l.destination === "SCRAP",
                            ).length
                          }
                        </span>
                        {totalDamage > 0 && (
                          <span style={{ color: "#f5222d" }}>
                            Damage: <strong>{fmtCur(totalDamage)}</strong>
                          </span>
                        )}
                      </div>
                    }
                  />
                );
              })()}
          </>
        )}

        {rentalDetail && lines.length === 0 && (
          <Alert
            type="success"
            message="All items from this rental have already been fully returned."
          />
        )}
      </Modal>

      {/* ── View Drawer ──────────────────────────────────────── */}
      <Drawer
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Return: {viewRec?.returnNo}</span>
            <Button
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => printBill(viewRec)}
              style={{ marginRight: 32 }}
            >
              Print Bill
            </Button>
          </div>
        }
        open={!!viewRec}
        onClose={() => setViewRec(null)}
        width={640}
      >
        {viewRec && (
          <>
            <Descriptions
              bordered
              size="small"
              column={2}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Return No" span={2}>
                <span className="mono">{viewRec.returnNo}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Customer" span={2}>
                <strong>{viewRec.customer?.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Return Date">
                {fmtDate(viewRec.returnDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Status">
                <StatusBadge status={viewRec.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Total Bill">
                <span className="mono">{fmtCurrency(viewRec.totalBill)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Offer">
                <span className="mono" style={{ color: "var(--warning)" }}>
                  -{fmtCurrency(viewRec.offer)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Net Bill" span={2}>
                <span
                  className="mono"
                  style={{ fontWeight: 700, fontSize: 15 }}
                >
                  {fmtCurrency(viewRec.netBill)}
                </span>
              </Descriptions.Item>
              {viewRec.notes && (
                <Descriptions.Item label="Notes" span={2}>
                  {viewRec.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">Return Items</Divider>
            <Table
              scroll={{ x: "max-content" }}
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
              rowKey="id"
              dataSource={viewRec.items || []}
              columns={[
                {
                  title: "Product",
                  render: (_, r) => (
                    <span className="mono">{r.rentalItem?.product?.name}</span>
                  ),
                },
                { title: "Qty", dataIndex: "qtyReturned" },
                {
                  title: "Period",
                  render: (_, r) =>
                    `${fmtDate(r.fromDate)} → ${fmtDate(r.toDate)}`,
                },
                {
                  title: "Rent Cost",
                  dataIndex: "cost",
                  render: (v) => <span className="mono">{fmtCurrency(v)}</span>,
                },
                {
                  title: "Damage",
                  dataIndex: "damageAmount",
                  render: (v) =>
                    v > 0 ? (
                      <span className="mono" style={{ color: "red" }}>
                        {fmtCurrency(v)}
                      </span>
                    ) : (
                      "—"
                    ),
                },
                {
                  title: "Condition",
                  dataIndex: "condition",
                  render: conditionTag,
                },
                {
                  title: "Destination",
                  dataIndex: "destination",
                  render: destTag,
                },
              ]}
            />

            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                background: "var(--surface2, #fafafa)",
                borderRadius: 6,
              }}
            >
              {(() => {
                const rentTotal = (viewRec.items || []).reduce(
                  (s, r) => s + Number(r.cost || 0),
                  0,
                );
                const damageTotal = (viewRec.items || []).reduce(
                  (s, r) => s + Number(r.damageAmount || 0),
                  0,
                );
                return (
                  <Space
                    direction="vertical"
                    size={2}
                    style={{
                      fontSize: 12,
                      fontFamily: "var(--font-mono)",
                      color: "#eae2db",
                    }}
                  >
                    <span>Rent: {fmtCur(rentTotal)}</span>
                    {damageTotal > 0 && (
                      <span style={{ color: "#f5222d" }}>
                        Damage: + {fmtCur(damageTotal)}
                      </span>
                    )}
                    <span>Gross: {fmtCur(Number(viewRec.totalBill || 0))}</span>
                    {Number(viewRec.offer) > 0 && (
                      <span style={{ color: "#fa8c16" }}>
                        Offer: - {fmtCur(Number(viewRec.offer))}
                      </span>
                    )}
                    <span style={{ fontWeight: 700, fontSize: 14 }}>
                      Net: {fmtCur(Number(viewRec.netBill || 0))}
                    </span>
                    <span style={{ color: "var(--text-muted)" }}>
                      Paid: {fmtCur(Number(viewRec.paidAmount || 0))}
                    </span>
                  </Space>
                );
              })()}
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}